import { useLayoutEffect, RefObject, MutableRefObject } from "react";
import MarkerCluster from "@googlemaps/markerclustererplus";
import { ClusterDetails, MapMarker } from "./useStationaryMarkers";
import { addModalTextToMarker, defaultClusterOptions, splitUpText } from "../utils";

/**
 * Markers that move on the map and need updating
 */
export interface MovingMapMarker extends MapMarker {
    id: string;
    modalClickLink?: string;
}

export type MovingMarkerRef = {
    [keys: string]: google.maps.Marker;
};

export const useMovingMarkers = (
    googleMapRef: RefObject<google.maps.Map>,
    movingMarkers: MovingMapMarker[],
    movingMarkerRef: MutableRefObject<MovingMarkerRef>,
    movingMarkerInfoWindowRef: MutableRefObject<{ [keys: string]: google.maps.InfoWindow }>,
    movingMarkerClusterRef: RefObject<MarkerCluster>,
    movingMarkerClusterDetails?: ClusterDetails,
    options?: google.maps.MapOptions,
) => {
    // Each time the moving markers are updated, we go through and update their position and
    // also their icon if those changes. We store the markers in a json object ref to keep
    // track it in between renders to avoid recreating the moving markers as these may move and
    // update often
    useLayoutEffect(() => {
        if (googleMapRef.current) {
            let updated = false;
            movingMarkers.forEach(
                ({
                    id, latLng, icon, zIndex, label, modalText, modalClickLink,
                }) => {
                    const currentMarker = movingMarkerRef.current[id];

                    if (currentMarker) {
                        const position = currentMarker.getPosition();

                        // When we update the lat lng of a moving marker we want to read
                        // it to the clusterer instance so it knows whether to recluster
                        if (position?.lat() !== latLng.lat || position?.lng() !== latLng.lng) {
                            movingMarkerRef.current[id].setPosition(latLng);
                            updated = true;
                        }

                        if (icon && currentMarker.getIcon()?.toString() !== icon.url) {
                            currentMarker.setIcon(icon);
                        }
                    } else {
                        movingMarkerRef.current[id] = new google.maps.Marker({
                            position: latLng,
                            map: googleMapRef.current,
                            icon,
                            zIndex,
                            label,
                        });

                        if (modalClickLink && movingMarkerRef.current[modalClickLink]) {
                            movingMarkerRef.current[id].addListener("click", () => {
                                google.maps.event.trigger(movingMarkerRef.current[modalClickLink], "click");
                            });
                        }

                        movingMarkerClusterRef.current?.addMarker(
                            movingMarkerRef.current[id],
                        );
                        updated = true;
                    }

                    // If there is some text associated with the marker we create an info window
                    // The state is stored in a closure below (which works due to js magic)
                    // and keeps track of the click so the user can toggle open and close
                    // on the marker
                    if (modalText && googleMapRef.current) {
                        const parsedModalText = splitUpText(modalText);
                        // We either create a new info window and store it,
                        // or we update the content of the info window if different
                        if (!movingMarkerInfoWindowRef.current[id]) {
                            movingMarkerInfoWindowRef.current[id] = addModalTextToMarker(
                                modalText,
                                googleMapRef.current,
                                movingMarkerRef.current[id],
                            );
                        } else if (
                            movingMarkerInfoWindowRef.current[id].getContent() !== parsedModalText
                        ) {
                            movingMarkerInfoWindowRef.current[modalClickLink || id]
                                .setContent(parsedModalText);
                        }
                    }
                },
            );

            // Loop through and remove old moving markers that arent there anymore
            Object.keys(movingMarkerRef.current).forEach((id) => {
                if (!movingMarkers.find(({ id: _id }) => _id === id)) {
                    movingMarkerClusterRef.current?.removeMarker(
                        movingMarkerRef.current[id],
                    );
                    movingMarkerRef.current[id]?.setMap(null);
                    delete movingMarkerRef.current[id];
                }
            });

            // If a moving marker was updated, redraw the clusters
            if (updated) {
                movingMarkerClusterRef.current?.repaint();
            }
        }
    }, [movingMarkers]);

    // Loops through and recalculates the moving markers and any new clusters that may result
    // from the markers getting too close together
    useLayoutEffect(() => {
        if (movingMarkerClusterDetails && googleMapRef.current) {
            const {
                icon, className, minimumClusterSize = 2, calculator,
            } = movingMarkerClusterDetails;

            (movingMarkerClusterRef.current as unknown) = new MarkerCluster(
                googleMapRef.current,
                Object.values(movingMarkerRef.current),
                {
                    ...defaultClusterOptions(icon, minimumClusterSize, className),
                    calculator,
                },
            );
        }
    }, [movingMarkerClusterDetails, options]);
};
